<template>
  <div v-if="embedded" class="contact-portal contact-portal--embedded">
    <v-progress-linear v-if="!iframeLoaded" indeterminate color="primary" />

    <div
      ref="iframeContainer"
      class="contact-portal__iframe-wrap"
      v-show="iframeLoaded"
    ></div>

    <div class="contact-portal__actions">
      <v-text-field
        :value="copyLink"
        class="no-label background-dark mr-2"
        readonly
        hide-details
      />

      <contact-portal-link-copy
        :copyLink="copyLink"
        :contactName="contactName"
        :pid="pid"
      />
    </div>
  </div>

  <modal-card
    v-else
    :title="pageTitle"
    :showActionsSpacer="false"
    class="contact-portal"
    fixed
    @close="$emit('close')"
  >
    <v-progress-linear v-if="!iframeLoaded" indeterminate color="primary" />

    <div
      ref="iframeContainer"
      class="contact-portal__iframe-wrap"
      v-show="iframeLoaded"
    ></div>

    <template v-slot:actions>
      <v-text-field
        :value="copyLink"
        class="no-label background-dark mr-2"
        readonly
      />

      <contact-portal-link-copy
        :copyLink="copyLink"
        :contactName="contactName"
        :pid="pid"
      />
    </template>
  </modal-card>
</template>

<script>
import { mapActions } from 'vuex';
import { connectToChild } from '@/lib/parentConnection';
import { appRoute } from '@/globals';
import localStore from 'store';

import ContactPortalLinkCopy from '@/views/contacts/contact/ContactPortalLinkCopy.vue';

export default {
  name: 'ContactPortal',
  components: {
    ContactPortalLinkCopy,
  },
  props: {
    contact: {
      type: Object,
      default: () => ({}),
    },
    embedded: Boolean,
  },
  data() {
    return {
      iframeLoaded: false,
      contactToken: '',
      currentUrl: null,
      contactPid: null,
      iframe: null,
      connection: null,
      requestId: 0,
    };
  },
  computed: {
    pageTitle() {
      return `${this.contactName}'s Portal`;
    },

    pid() {
      return this.contact?.con_pid || this.contactPid;
    },

    contactId() {
      return this.contact?.con_id;
    },

    contactName() {
      const { first_name = '', last_name = '', name } = this.contact || {};
      return name || `${first_name} ${last_name}`.trim() || 'Unknown';
    },

    portalHomeUrl() {
      if (!this.pid) return;

      return appRoute(`/portal/${this.pid}`, true);
    },

    copyLink() {
      return this.currentUrl || this.portalHomeUrl;
    },
  },
  watch: {
    contactId: {
      immediate: true,
      handler(contactId) {
        this.loadPortal(contactId);
      },
    },
  },
  methods: {
    ...mapActions(['loadSingleContact', 'getContactToken']),

    async loadPortal(contactId) {
      const requestId = ++this.requestId;
      this.destroyPortal();
      this.clearPreviousPortalAuth();
      this.iframeLoaded = false;
      this.contactToken = '';
      this.contactPid = null;
      this.currentUrl = null;
      if (!contactId) return;

      try {
        const [token, loadedContact] = await Promise.all([
          this.getContactToken(contactId),
          this.contact?.con_pid ? null : this.loadSingleContact(contactId),
        ]);
        if (requestId !== this.requestId) return;

        this.contactToken = token;
        this.contactPid = this.contact?.con_pid || loadedContact?.con_pid;
        await this.initPortalIframe(requestId);
      } catch (error) {
        if (requestId === this.requestId) this.iframeLoaded = true;
      }
    },

    async initPortalIframe(requestId) {
      await this.$nextTick();

      if (requestId !== this.requestId || !this.contactToken || !this.pid) {
        return;
      }

      const iframe = document.createElement('iframe');
      iframe.setAttribute('frameBorder', 0);
      const iframeContainer = this.$refs.iframeContainer;
      if (!iframeContainer) return;
      this.iframe = iframe;

      const portalAuth = {
        id: this.pid,
        token: this.contactToken,
      };

      localStore.set('portal_auth', portalAuth);

      const options = {
        url: this.portalHomeUrl,
        appendTo: iframeContainer,
        methods: {
          navigate: (url) => {
            this.setCurrentUrl(url);
          },
        },
        iframe: iframe,
      };

      try {
        const connection = await connectToChild(options);
        if (requestId !== this.requestId) {
          connection.destroy();
          return;
        }
        this.connection = connection;
        const portal = await connection.promise;

        if (portal && requestId === this.requestId) {
          portal.setAuth(portalAuth);
        }
      } catch (e) {
        if (requestId === this.requestId) {
          console.error('Error loading contact portal', e);
        }
      } finally {
        if (requestId === this.requestId) this.iframeLoaded = true;
      }
    },

    destroyPortal() {
      if (this.connection) {
        this.connection.destroy();
        this.connection = null;
      }
      if (this.iframe) {
        this.iframe.remove();
        this.iframe = null;
      }
    },

    setCurrentUrl(url) {
      if (!url || this.portalHomeUrl.endsWith(url) || url === '/portal/') {
        this.currentUrl = this.portalHomeUrl;
        return;
      }

      const cleanUrl = url.replace(/\/portal/g, '');
      this.currentUrl = `${this.portalHomeUrl}${cleanUrl}`;
    },

    clearPreviousPortalAuth() {
      localStore.remove('portal_auth');
    },
  },
  beforeDestroy() {
    this.requestId += 1;
    this.destroyPortal();
    this.clearPreviousPortalAuth();
  },
};
</script>

<style lang="less">
.contact-portal__iframe-wrap {
  margin: 0 auto;
  height: 100%;
  width: 100%;
  position: relative;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background-color: var(--v-itemBackground-base);
  }
}

.contact-portal--embedded {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;

  .contact-portal__iframe-wrap {
    flex: 1;
    min-height: 0;
  }
}

.contact-portal__actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  padding: 12px 16px;
  margin-top: auto;
  border-top: 1px solid var(--v-borderColorDark-base);
  background: var(--v-itemBackground-base);
}
</style>
